import React, { useState } from 'react';
import { motion } from 'framer-motion';
import AdminLayout from '../layout/AdminLayout';
import { RefreshCw, Clock } from 'lucide-react';
import { Button } from '../ui/button';

// Dashboard components
import { useDashboardData } from './dashboard/hooks/useDashboardData';
import { TimeRangeFilter } from './dashboard/widgets/TimeRangeFilter';
import { KPIOverviewRow } from './dashboard/sections/KPIOverviewRow';
import { SystemHealthPanel } from './dashboard/sections/SystemHealthPanel';
import { ActionRequiredPanel } from './dashboard/sections/ActionRequiredPanel';
import { BusinessFunnel } from './dashboard/sections/BusinessFunnel';
import { OperationalPanels } from './dashboard/sections/OperationalPanels';
import { RecentActivityFeeds } from './dashboard/sections/RecentActivityFeeds';

export default function SuperAdminDashboard() {
  const [period, setPeriod] = useState('30d');
  const [lastUpdated, setLastUpdated] = useState(new Date());

  const {
    kpis,
    health,
    funnel,
    alerts,
    topAgents,
    counselorWorkload,
    topUniversities,
    topCountries,
    trends,
    recentActivity,
    contentMetrics,
    loading,
    refresh
  } = useDashboardData(period);

  const handleRefresh = () => {
    refresh();
    setLastUpdated(new Date());
  };

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    setLastUpdated(new Date());
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="super-admin-dashboard">
        {/* Dashboard Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        >
          <div>
            <h1 className="text-2xl font-bold text-gray-900 font-outfit">Dashboard</h1>
            <div className="flex items-center gap-2 mt-1">
              <Clock className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-xs text-gray-500">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <TimeRangeFilter value={period} onChange={handlePeriodChange} />
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={loading}
              className="gap-2 bg-white border-stone-200 hover:bg-stone-50 text-gray-700"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </motion.div>

        {/* KPI Overview Row - Section A */}
        <section>
          <KPIOverviewRow kpis={kpis} loading={loading} />
        </section>

        {/* Action Required & System Health - Sections B & E */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <ActionRequiredPanel alerts={alerts} loading={loading} />
          <SystemHealthPanel health={health} loading={loading} />
        </section>

        {/* Business Funnel - Section C */}
        <section>
          <BusinessFunnel funnel={funnel} loading={loading} />
        </section>

        {/* Operational Panels - Section D */}
        <section>
          <OperationalPanels
            topAgents={topAgents}
            counselorWorkload={counselorWorkload}
            topUniversities={topUniversities}
            topCountries={topCountries}
            trends={trends}
            contentMetrics={contentMetrics}
            loading={loading}
          />
        </section>

        {/* Recent Activity Feeds - Section F */}
        <section>
          <RecentActivityFeeds recentActivity={recentActivity} loading={loading} />
        </section>
      </div>
    </AdminLayout>
  );
}
